import React from 'react';
import { Link2 } from 'lucide-react';
import { Input } from '../../ui/Input';
import { Select } from '../../ui/Select';
import { SearchableSelect } from '../../ui/SearchableSelect';
import { ChoiceGroup } from '../ChoiceGroup';
import { mockData } from '../../../services/mockData';
import { trainingProducts } from '../../../data/trainingProducts';
import { courseCharacteristics, courseCommissions, courseStatusLabels, courseVenues } from '../../../data/trainingCourseOptions';
import { getClientSites } from '../../../utils/clientSites';
import { getTrainedCourses } from '../../../utils/trainingCourses';
import { CourseCharacteristic, CourseCommission, CourseErrors, CourseStatus, CourseVenue, TrainingCourse, TrainingCourseDraft } from '../../../types/trainingCourse';
interface CourseDetailsFieldsProps {
  draft: TrainingCourseDraft;
  onChange: (changes: Partial<TrainingCourseDraft>) => void;
  errors: CourseErrors;
  courses: TrainingCourse[];
}
export function CourseDetailsFields({
  draft,
  onChange,
  errors,
  courses
}: CourseDetailsFieldsProps) {
  const isOnDemand = draft.status === 'ondemand';
  const clientOptions = mockData.getClients().map(c => ({
    value: c.name,
    label: c.name
  }));
  const productOptions = trainingProducts.map(p => ({
    value: p.code,
    label: `${p.type} — ${p.name} · ${p.code}`
  }));
  const clientSites = getClientSites(draft.clientName);
  const siteOptions = draft.clientSite && !clientSites.includes(draft.clientSite) ? [...clientSites, draft.clientSite] : clientSites;
  // "Sede cliente" is not available for SEFORM commissions
  const venueOptions = courseVenues.filter(v => !(v.value === 'cliente' && draft.commission === 'SEFORM'));
  const leadOptions = courses.filter(c => c.characteristic === 'Trainante' && c.id !== draft.id).map(c => ({
    value: c.id,
    label: `${c.courseCode} — ${c.name}`
  }));
  const trainedCourses = getTrainedCourses(courses, draft.id);
  const handleStatus = (status: CourseStatus) => {
    onChange({
      status,
      // Restore defaults when leaving "on-demand"
      ...(status !== 'ondemand' && !draft.commission ? {
        commission: 'SEFORM' as CourseCommission
      } : {}),
      ...(status !== 'ondemand' && !draft.venue ? {
        venue: 'seform' as CourseVenue
      } : {})
    });
  };
  const handleCommission = (commission: CourseCommission) => {
    onChange({
      commission,
      ...(commission === 'SEFORM' ? {
        clientName: undefined,
        clientSite: undefined
      } : {}),
      ...(commission === 'SEFORM' && draft.venue === 'cliente' ? {
        venue: 'seform' as CourseVenue
      } : {})
    });
  };
  const handleCharacteristic = (characteristic: CourseCharacteristic) => {
    onChange({
      characteristic,
      leadCourseId: characteristic === 'Trainato' ? draft.leadCourseId : undefined
    });
  };
  return <div className="space-y-8" data-id="element-1110" data-mp="ypxaatwnqw1d">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1111" data-mp="yp5m445e40l4">
        <div className="col-span-full" data-id="element-1112" data-mp="2ja2ee0tfce75">
          <Input label="Nome corso" placeholder="Es. Formazione generale lavoratori — Sessione ottobre" value={draft.name} onChange={e => onChange({
          name: e.target.value
        })} data-id="element-1113" data-mp="2j6y1mf1w8a4v" />
        </div>
        <div className="col-span-full" data-id="element-1114" data-mp="2o7cbm2n21u5j">
          <SearchableSelect label="Tipologia di corso + codice prodotto" value={draft.productCode} onChange={e => onChange({
          productCode: e.target.value
        })} options={productOptions} placeholder="Cerca tipologia o codice prodotto..." error={errors.productCode} required data-id="element-1115" data-mp="2o6apn34i3n91" />
        </div>
        <div data-id="element-1116" data-mp="36zejoplms246">
          <Input label="Codice specifico del corso" placeholder="Es. FGL-2026-10" value={draft.courseCode} onChange={e => onChange({
          courseCode: e.target.value
        })} error={errors.courseCode} required data-id="element-1117" data-mp="36wscws6kdqn0" />
          <label className="mt-3 flex items-center gap-2 cursor-pointer w-fit" data-id="element-1118" data-mp="3ndwaunl2d2i">
            <input type="checkbox" className="rounded border-slate-300 text-seform-orange focus:ring-seform-orange" checked={draft.externalProduct} onChange={e => onChange({
            externalProduct: e.target.checked
          })} data-id="element-1119" data-mp="8lp30wil07gi" />
            <span className="text-sm text-slate-700" data-id="element-1120" data-mp="3h3l8rt68r818">
              Acquisto prodotto esterno
            </span>
          </label>
        </div>
        <Select label="Stato del corso" value={draft.status} onChange={e => handleStatus(e.target.value as CourseStatus)} options={(Object.keys(courseStatusLabels) as CourseStatus[]).map(s => ({
        value: s,
        label: courseStatusLabels[s]
      }))} data-id="element-1121" data-mp="3m24ym4nyh3yv" />
        <Input label="Data inizio corso" type="date" value={draft.startDate || ''} onChange={e => onChange({
        startDate: e.target.value || undefined
      })} data-id="element-1122" data-mp="2yahk6m7vrldh" />
        <Input label="Data fine corso" type="date" value={draft.endDate || ''} min={draft.startDate} onChange={e => onChange({
        endDate: e.target.value || undefined
      })} error={errors.endDate} data-id="element-1123" data-mp="2pva89b3h5i53" />
      </div>

      <div className="h-px bg-slate-100" data-id="element-1124" data-mp="2gubysda9oif4"></div>

      <section className="space-y-4" data-id="element-1125" data-mp="2bw2381kmrfsz">
        <ChoiceGroup label="Caratteristica del corso" name="course-characteristic" value={draft.characteristic} options={courseCharacteristics.map(c => ({
        value: c,
        label: c
      }))} onChange={handleCharacteristic} data-id="element-1126" data-mp="2bx3laq02d2k7" />
        {draft.characteristic === 'Trainante' && <div className="rounded-xl bg-slate-50 border border-slate-200 p-4" data-id="element-1127" data-mp="1y2awf0yaaa0y">
            <p className="text-sm font-medium text-seform-navy flex items-center gap-2 mb-2" data-id="element-1128" data-mp="1t49am89h3wls">
              <Link2 className="h-4 w-4" data-id="element-1129" data-mp="1o6pz3g45txxj" /> Corsi trainati
            </p>
            {trainedCourses.length > 0 ? <ul className="space-y-1.5" data-id="element-1130" data-mp="1ekor0121u3lw">
                {trainedCourses.map(c => <li key={c.id} className="text-sm text-slate-700" data-id="element-1131" data-mp="1jfc4z5zfffm8">
                    <span className="font-mono text-xs text-slate-500 mr-2" data-id="element-1132" data-mp="14lfzdeunp12p">
                      {c.courseCode}
                    </span>
                    {c.name}
                  </li>)}
              </ul> : <p className="text-sm text-slate-500" data-id="element-1133" data-mp="227wvwhvkfane">
                Nessun corso trainato. I corsi che sceglieranno questo corso
                come trainante compariranno qui in automatico.
              </p>}
          </div>}
        {draft.characteristic === 'Trainato' && <SearchableSelect label="Corso trainante" value={draft.leadCourseId || ''} onChange={e => onChange({
        leadCourseId: e.target.value
      })} options={leadOptions} placeholder="Cerca e seleziona il corso trainante..." error={errors.leadCourseId} required data-id="element-1134" data-mp="2g7o4g33bbyu0" />}
      </section>

      {!isOnDemand && draft.commission && draft.venue && <>
          <div className="h-px bg-slate-100" data-id="element-1135" data-mp="j7xdkxr94611"></div>
          <section className="space-y-4" data-id="element-1136" data-mp="ea5nm9a62tzh">
            <ChoiceGroup label="Commessa" name="course-commission" value={draft.commission} options={courseCommissions} onChange={handleCommission} data-id="element-1137" data-mp="ebgnvz4eljlu" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1138" data-mp="3hsyww9vwwwnu">
              {draft.commission === 'Cliente' && <SearchableSelect label="Azienda" value={draft.clientName || ''} onChange={e => onChange({
            clientName: e.target.value,
            clientSite: undefined
          })} options={clientOptions} placeholder="Cerca e seleziona un'azienda..." error={errors.clientName} required data-id="element-1139" data-mp="3rp1ynemuqta9" />}
              <Select label="Sede" value={draft.venue} onChange={e => onChange({
            venue: e.target.value as CourseVenue,
            clientSite: e.target.value === 'cliente' ? draft.clientSite : undefined
          })} options={venueOptions} data-id="element-1140" data-mp="3aeojncak8m31" />
              {draft.venue === 'cliente' && <Select label="Sede del cliente" value={draft.clientSite || ''} onChange={e => onChange({
            clientSite: e.target.value
          })} disabled={!draft.clientName} error={errors.clientSite} description={draft.clientName ? 'Sede legale e sedi operative da anagrafica' : "Seleziona prima l'azienda"} options={[{
            value: '',
            label: 'Seleziona sede...'
          }, ...siteOptions.map(s => ({
            value: s,
            label: s
          }))]} required data-id="element-1141" data-mp="1ylcd7nd0zf8" />}
            </div>
          </section>
        </>}
    </div>;
}